import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  IconButton, InputAdornment, InputLabel, MenuItem, Paper, Rating,
  Select, SelectChangeEvent, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert, Button,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Close, Delete as DeleteIcon, Email as EmailIcon, Phone as PhoneIcon,
  LocationOn as LocationIcon, Search,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';
import LoadingAnimation from '../components/LoadingAnimation';

interface Business {
  _id?: string;
  id?: string;
  name: string;
  description: string;
  category: string;
  location: any;
  contact: any;
  status: 'pending' | 'verified' | 'rejected' | 'suspended';
  rating?: { average: number; count: number };
  activeBoxes?: number;
  totalSales?: number;
  owner?: any;
  logo?: string;
  images?: string[];
  cuisineTags?: string[];
}

export default function Businesses() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; verified: number; pending: number; rejected: number; suspended: number } | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  // Status/search now filter server-side (the table is paginated, so
  // filtering only the fetched page hid businesses that weren't among the
  // rows on the current page). Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedQuery]);

  useEffect(() => {
    fetchBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, debouncedQuery]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get('/api/admin/businesses', {
        params: {
          page,
          limit: rowsPerPage,
          status: statusFilter !== 'All' ? statusFilter : undefined,
          search: debouncedQuery || undefined,
        },
      });
      setBusinesses(response.data.businesses || []);
      setTotal(response.data.total ?? response.data.businesses?.length ?? 0);
      setServerStats(response.data.stats || null);
      setError(false);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // The backend already applied the status/search filters and pagination,
  // so this page's rows ARE the filtered results -- no client-side re-filtering.
  const filteredBusinesses = businesses;

  // Prefer the backend's platform-wide counts (every business, not just
  // this page) so the stat cards don't undercount once there's more than
  // one page of businesses.
  const stats = serverStats || {
    total: businesses.length,
    verified: businesses.filter(b => b.status === 'verified').length,
    pending: businesses.filter(b => b.status === 'pending').length,
    rejected: businesses.filter(b => b.status === 'rejected').length,
    suspended: businesses.filter(b => b.status === 'suspended').length,
  };

  const handleVerify = async (businessId: string) => {
    try {
      await axios.put(`/api/admin/businesses/${businessId}/verify`);
      fetchBusinesses();
      setSnackbar({ open: true, message: 'Business verified successfully', severity: 'success' });
      setDetailsOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to verify business', severity: 'error' });
    }
  };

  const handleReject = async () => {
    if (!selectedBusiness) return;
    try {
      await axios.put(`/api/businesses/${selectedBusiness._id || selectedBusiness.id}`, {
        status: 'rejected',
        rejectionReason: rejectReason,
      });
      fetchBusinesses();
      setSnackbar({ open: true, message: 'Business rejected successfully', severity: 'success' });
      setRejectDialogOpen(false);
      setDetailsOpen(false);
      setRejectReason('');
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reject business', severity: 'error' });
    }
  };

  const handleSuspend = async (businessId: string) => {
    try {
      await axios.put(`/api/admin/businesses/${businessId}/suspend`);
      fetchBusinesses();
      setSnackbar({ open: true, message: 'Business suspended and removed from the marketplace', severity: 'success' });
      setDetailsOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to suspend business', severity: 'error' });
    }
  };

  const handleRemove = async () => {
    const businessId = selectedBusiness?._id || selectedBusiness?.id;
    if (!businessId) return;
    try {
      await axios.delete(`/api/admin/businesses/${businessId}`);
      await fetchBusinesses();
      setSnackbar({ open: true, message: 'Business removed from the marketplace', severity: 'success' });
      setRemoveDialogOpen(false);
      setDetailsOpen(false);
      setSelectedBusiness(null);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove business', severity: 'error' });
    }
  };

  const handleViewDetails = (business: Business) => {
    setSelectedBusiness(business);
    setDetailsOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Business Management</Typography>
          <Typography color="text.secondary">Review and verify merchant applications.</Typography>
        </Box>
        <Button variant="contained" color={stats.pending > 0 ? 'warning' : 'inherit'}>
          Pending ({stats.pending})
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Businesses</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Verified</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.verified}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Pending</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.pending}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Suspended</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{stats.suspended}</Typography>
        </Paper>
      </Box>

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search businesses"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              {['All', 'pending', 'verified', 'rejected', 'suspended'].map((opt) => (
                <MenuItem key={opt} value={opt === 'All' ? 'All' : opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>Unable to load businesses. Check that the backend is running.</Alert>}

        {!error && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Logo</strong></TableCell>
                  <TableCell><strong>Business Name</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Rating</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <LoadingAnimation message="Loading businesses..." subtitle="Fetching merchant data" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredBusinesses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No businesses found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredBusinesses.map((business) => {
                  const businessId = business._id || business.id;
                  return (
                    <TableRow key={businessId} hover>
                      <TableCell>
                        <Avatar
                          src={business.logo || business.images?.[0]}
                          alt={business.name}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        >
                          {business.name?.[0]}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{business.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{business.description?.substring(0, 50)}</Typography>
                        {!!business.cuisineTags?.length && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            {business.cuisineTags.slice(0, 3).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>{business.category}</TableCell>
                      <TableCell>{business.location?.address || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={business.status}
                          color={
                            business.status === 'verified' ? 'success' :
                              business.status === 'pending' ? 'warning' :
                                business.status === 'rejected' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={business.rating?.average || 0} precision={0.5} size="small" readOnly />
                          <Typography variant="caption">{business.rating?.average?.toFixed(1) || '0.0'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleViewDetails(business)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* Business Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Business Details
          <IconButton onClick={() => setDetailsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedBusiness && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {(selectedBusiness.logo || selectedBusiness.images?.[0]) && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Avatar
                    src={selectedBusiness.logo || selectedBusiness.images?.[0]}
                    alt={selectedBusiness.name}
                    variant="rounded"
                    sx={{ width: 96, height: 96 }}
                  />
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Business Name</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedBusiness.name}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography>{selectedBusiness.description}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Chip label={selectedBusiness.category} size="small" />
              </Box>

              {!!selectedBusiness.cuisineTags?.length && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Cuisine</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {selectedBusiness.cuisineTags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Contact Information</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" />
                    <Typography>{selectedBusiness.contact?.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" />
                    <Typography>{selectedBusiness.contact?.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" />
                    <Typography>{selectedBusiness.location?.address}</Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedBusiness.status}
                  color={
                    selectedBusiness.status === 'verified' ? 'success' :
                      selectedBusiness.status === 'pending' ? 'warning' :
                        selectedBusiness.status === 'rejected' ? 'error' : 'default'
                  }
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Rating</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Rating value={selectedBusiness.rating?.average || 0} precision={0.5} size="small" readOnly />
                  <Typography>({selectedBusiness.rating?.count || 0} reviews)</Typography>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setRemoveDialogOpen(true)}
          >
            Remove
          </Button>
          {selectedBusiness?.status === 'pending' && (
            <>
              <Button color="error" onClick={() => setRejectDialogOpen(true)}>
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleVerify(selectedBusiness._id || selectedBusiness.id || '')}
              >
                Verify
              </Button>
            </>
          )}
          {selectedBusiness?.status === 'verified' && (
            <Button color="error" onClick={() => handleSuspend(selectedBusiness._id || selectedBusiness.id || '')}>
              Suspend
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Removing is intentionally a soft delete so past order records stay valid. */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove business?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {selectedBusiness?.name} and all of its products will be removed from the marketplace.
            Existing orders will be kept for reporting and customer history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleRemove}>
            Remove business
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Business</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Explain why this business is being rejected..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={!rejectReason}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
